/**
 * Staff and Skills API Service
 * 
 * Handles all API calls for:
 * - Staff/Employee management
 * - Skills and competencies
 * - Capacity tracking
 * - Themis AI recommendations
 */

import { buildApiUrl, getDefaultHeaders } from './api';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export type RecommendationType = 'spof' | 'bottleneck' | 'skill-gap' | 'succession-risk' | 'capacity-shortage' | 'over-allocation';
export type SeverityLevel = 'critical' | 'high' | 'medium' | 'low';

export interface ThemisRecommendation {
  id: string;
  domain: string;
  type: RecommendationType;
  severity: SeverityLevel;
  title: string;
  description: string;
  action: string;
  impact: string;
  confidence: number;
  context: {
    staffMember?: string;
    skill?: string;
    department?: string;
    utilization?: number;
    expertiseScore?: number;
    isBottleneck?: boolean;
    suggestedCandidates?: string[];
    [key: string]: any;
  };
  createdAt: string;
}

export interface ThemisRecommendationsResponse {
  domain: string;
  recommendations: ThemisRecommendation[];
  summary: {
    total: number;
    critical: number;
    high: number;
    medium: number;
    low: number;
  };
  generatedAt: string;
}

export interface SkillEvidence {
  certificateName?: string;
  certificateIssuer?: string;
  certificateNumber?: string;
  certificateExpiryDate?: string;
  yearsExperience?: number;
  assessmentScore?: number;
  assessmentDate?: string;
  educationDegree?: string;
  educationInstitution?: string;
  educationGraduationYear?: number;
}

export interface StaffSkill {
  skillId: string;
  skillName: string;
  proficiencyLevel: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  allocatedHours: number;
  evidence?: SkillEvidence;
}

export interface Staff {
  id: string;
  employeeId: string;
  name: string;
  email: string;
  role: string;
  department: string;
  seniority?: 'Junior' | 'Mid-level' | 'Senior' | 'Lead' | 'Principal';
  status: 'active' | 'inactive' | 'on-leave';
  location: string;
  totalCapacityHours: number;
  availableCapacityHours: number;
  utilizationPercent: number;
  skills: StaffSkill[];
  processes: string[];
  customers: string[];
  createdAt: string;
  updatedAt: string;
}

export interface Skill {
  id: string;
  name: string;
  category: string;
  description: string;
  requiredForProcesses: string[];
  staffCount: number;
  avgProficiency: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  pagination?: {
    page: number;
    pageSize: number;
    totalItems: number;
    totalPages: number;
  };
}

// ============================================================================
// SKILLS API
// ============================================================================

/**
 * Get all skills with optional filtering
 */
export async function getSkills(params?: {
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<Skill[]>> {
  const queryParams = new URLSearchParams();
  if (params?.category) queryParams.append('category', params.category);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

  const url = `${buildApiUrl('/skills')}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch skills: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single skill by ID
 */
export async function getSkillById(id: string): Promise<ApiResponse<Skill>> {
  const response = await fetch(buildApiUrl(`/skills?id=${id}`), {
    method: 'GET',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch skill: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new skill
 */
export async function createSkill(skill: {
  name: string;
  category: string;
  description: string;
  requiredForProcesses?: string[];
  staffCount?: number;
  avgProficiency?: string;
}): Promise<ApiResponse<Skill>> {
  const response = await fetch(buildApiUrl('/skills'), {
    method: 'POST',
    headers: getDefaultHeaders(),
    body: JSON.stringify(skill),
  });

  if (!response.ok) {
    throw new Error(`Failed to create skill: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a skill
 */
export async function updateSkill(id: string, updates: Partial<Skill>): Promise<ApiResponse<Skill>> {
  const response = await fetch(buildApiUrl(`/skills?id=${id}`), {
    method: 'PUT',
    headers: getDefaultHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update skill: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a skill
 */
export async function deleteSkill(id: string): Promise<ApiResponse<void>> {
  const response = await fetch(buildApiUrl(`/skills?id=${id}`), {
    method: 'DELETE',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete skill: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// STAFF API
// ============================================================================

/**
 * Get all staff with optional filtering
 */
export async function getStaff(params?: {
  status?: string;
  department?: string;
  role?: string;
  location?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<ApiResponse<Staff[]>> {
  const queryParams = new URLSearchParams();
  if (params?.status) queryParams.append('status', params.status);
  if (params?.department) queryParams.append('department', params.department);
  if (params?.role) queryParams.append('role', params.role);
  if (params?.location) queryParams.append('location', params.location);
  if (params?.search) queryParams.append('search', params.search);
  if (params?.page) queryParams.append('page', params.page.toString());
  if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

  const url = `${buildApiUrl('/staff')}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  
  const response = await fetch(url, {
    method: 'GET',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch staff: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Get a single staff member by ID
 */
export async function getStaffById(id: string): Promise<ApiResponse<Staff>> {
  const response = await fetch(buildApiUrl(`/staff?id=${id}`), {
    method: 'GET',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch staff member: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Create a new staff member
 */
export async function createStaff(staff: {
  employeeId: string;
  name: string;
  email: string;
  role: string;
  department: string;
  seniority?: 'Junior' | 'Mid-level' | 'Senior' | 'Lead' | 'Principal';
  status?: string;
  location: string;
  totalCapacityHours?: number;
  availableCapacityHours?: number;
  utilizationPercent?: number;
  skills?: StaffSkill[];
  processes?: string[];
  customers?: string[];
}): Promise<ApiResponse<Staff>> {
  const response = await fetch(buildApiUrl('/staff'), {
    method: 'POST',
    headers: getDefaultHeaders(),
    body: JSON.stringify(staff),
  });

  if (!response.ok) {
    throw new Error(`Failed to create staff member: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Update a staff member
 */
export async function updateStaff(id: string, updates: Partial<Staff>): Promise<ApiResponse<Staff>> {
  const response = await fetch(buildApiUrl(`/staff?id=${id}`), {
    method: 'PUT',
    headers: getDefaultHeaders(),
    body: JSON.stringify(updates),
  });

  if (!response.ok) {
    throw new Error(`Failed to update staff member: ${response.statusText}`);
  }

  return response.json();
}

/**
 * Delete a staff member
 */
export async function deleteStaff(id: string): Promise<ApiResponse<void>> {
  const response = await fetch(buildApiUrl(`/staff?id=${id}`), {
    method: 'DELETE',
    headers: getDefaultHeaders(),
  });

  if (!response.ok) {
    throw new Error(`Failed to delete staff member: ${response.statusText}`);
  }

  return response.json();
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate staff utilization percentage
 */
export function calculateUtilization(totalHours: number, availableHours: number): number {
  if (totalHours === 0) return 0;
  return Math.round(((totalHours - availableHours) / totalHours) * 100);
}

/**
 * Get staff members with a specific skill
 */
export async function getStaffBySkill(skillId: string): Promise<Staff[]> {
  const response = await getStaff();
  if (!response.success || !response.data) {
    return [];
  }
  
  return response.data.filter(staff => 
    staff.skills.some(skill => skill.skillId === skillId)
  );
}

/**
 * Get all unique departments from staff list
 */
export async function getDepartments(): Promise<string[]> {
  const response = await getStaff();
  if (!response.success || !response.data) {
    return [];
  }
  
  const departments = new Set(response.data.map(staff => staff.department));
  return Array.from(departments).sort();
}

/**
 * Get all unique skill categories
 */
export async function getSkillCategories(): Promise<string[]> {
  const response = await getSkills();
  if (!response.success || !response.data) {
    return [];
  }
  
  const categories = new Set(response.data.map(skill => skill.category));
  return Array.from(categories).sort();
}

// ============================================================================
// SKILLS MATRIX & INTELLIGENCE
// ============================================================================

export interface ExpertiseScoreBreakdown {
  proficiencyScore: number;
  experienceScore: number;
  certificationScore: number;
  spofScore: number;
}

export interface StaffSkillScore {
  skillId: string;
  skillName: string;
  skillCategory: string;
  expertiseScore: number;
  proficiencyLevel: string;
  scoreBreakdown: ExpertiseScoreBreakdown;
  isSPOF: boolean;
  isBottleneck: boolean;
}

export interface MatrixStaff {
  staffId: string;
  staffName: string;
  role: string;
  department: string;
  utilizationPercent: number;
  skills: StaffSkillScore[];
}

export interface GapAnalysis {
  skillId: string;
  skillName: string;
  category: string;
  avgScore: number;
  staffCount: number;
  riskLevel: 'critical' | 'high' | 'medium';
}

export interface SPOFRisk {
  staffId: string;
  staffName: string;
  skillId: string;
  skillName: string;
  expertiseScore: number;
  isBottleneck: boolean;
}

export interface MatrixResponse {
  matrix: MatrixStaff[];
  gapAnalysis: GapAnalysis[];
  spofRisks: SPOFRisk[];
  summary: {
    totalStaff: number;
    totalSkills: number;
    avgExpertiseScore: number;
    skillsAtRisk: number;
    spofCount: number;
    bottleneckCount: number;
  };
}

/**
 * Get Skills Intelligence - Comprehensive analytics combining matrix, gaps, SPOF, and succession risks
 * Endpoint: GET /api/themis/analytics/skills
 */
export async function getSkillsIntelligence(): Promise<ApiResponse<MatrixResponse>> {
  try {
    const response = await fetch(
      buildApiUrl('/themis/analytics/skills'),
      {
        method: 'GET',
        headers: getDefaultHeaders(),
      }
    );

    if (!response.ok) {
      return {
        success: false,
        error: `Failed to fetch skills intelligence: ${response.statusText}`,
      };
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching skills intelligence:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error fetching skills intelligence',
    };
  }
}

/**
 * @deprecated Use getSkillsIntelligence() instead
 * Legacy alias for backwards compatibility
 */
export const getSkillsMatrix = getSkillsIntelligence;

/**
 * Get Themis AI recommendations for a specific domain
 * 
 * @param domain - The domain to get recommendations for (skills, risks, compliance, etc.)
 * @returns Themis recommendations response
 */
export async function getThemisRecommendations(domain: string = 'skills'): Promise<ThemisRecommendationsResponse> {
  try {
    const url = buildApiUrl(`/themis/recommendations?domain=${domain}`);
    const response = await fetch(url, {
      method: 'GET',
      headers: getDefaultHeaders(),
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch Themis recommendations: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error('Error fetching Themis recommendations:', error);
    throw error;
  }
}
